import {KMDFSIntro} from "./KMDFSIntro";
import {gsap} from "gsap";
import {KennethMustDie} from "../../KennethMustDie";
import {KMDGameMode} from "../../server/KMDGameMode";
import {IFeatureInfo, KMD_SYMBOL_ANIMATIONS, SPECIAL_SYMBOL_NAMES} from "../../server/IKMDData";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {KMDSound} from "../../sound/KMDSound";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class KMDCFSIntro extends KMDFSIntro {

    private readonly _featureExplainS2: PIXI.spine.Spine;
    private readonly _featureExplainS2Lock: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "KMDCFSIntro";

        const s2: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(SPECIAL_SYMBOL_NAMES.S2)),
            lock: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("locks"));

        s2.state.setAnimation(0, KMD_SYMBOL_ANIMATIONS.NORMAL, false);
        KennethMustDie.createTimelineForAnimation(lock, 0, "locked_150_start", false).totalProgress(1);

        s2.visible = lock.visible = false;
        this.addChild(s2, lock);

        this._featureExplainS2 = s2;
        this._featureExplainS2Lock = lock;
    }

    protected getFeatureExplainTl(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            const s2Feature: IFeatureInfo = KennethMustDie.model.s2Feature;

            this._featureExplainS2.position.set(((s2Feature.originReelId * KennethMustDie.SYMBOL_WIDTH) + KennethMustDie.SYMBOL_WIDTH / 2), (s2Feature.originSymId * KennethMustDie.SYMBOL_HEIGHT) + KennethMustDie.MAIN_REELS_TOP);
            this._featureExplainS2Lock.position = this._featureExplainS2.position;

            this._featureExplainS2.alpha = this._featureExplainS2Lock.alpha = 1;
            this._featureExplainS2.visible = this._featureExplainS2Lock.visible = true;
        });
        tl.add(super.getFeatureExplainTl());
        tl.add(this.xNudgeFeatureExplain());

        return tl;
    }

    protected getAssetName(): string {
        return ("cfsIntro");
    }

    protected playIntroSound(): void {
        KennethMustDie.soundPlayer.playSound(KMDSound.CFS_INTRO_IMPACT);

        KennethMustDie.soundPlayer.switchMusic("cfsIntro");
    }

    protected playBtnSound(): void {
        KennethMustDie.soundPlayer.playSound(KMDSound.BTN_START_CFS);
    }

    protected checkForUpgrade(): boolean {
        return (KennethMustDie.model.gameData.mode === KMDGameMode.FREESPIN || KennethMustDie.model.gameData.mode === KMDGameMode.FREESPIN_AVALANCHE);
    }

    protected getOutroNameForUpgrade(): string {
        return "FS_Outro";
    }

    // #281
    protected getPreTransition(): void {
        KennethMustDie.reelView.turnOffForCFS();
        KennethMustDie.specialReelView.turnOffForCFS();
        KennethMustDie.multipliersChain.turnOffForCFS();
        KennethMustDie.reelFrame.turnOffForCFS();
        KennethMustDie.possibleWays.turnOffForCFS();
        KennethMustDie.logo.turnOffForCFS();
    }

    // #281
    protected getTransition(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 3,
            postDuration: number = 0.2;

        tl.to(this, {
            duration: duration / 2
        });

        tl.add([
            KennethMustDie.backgroundElements.transitionForCFS(duration),
            KennethMustDie.reelFrame.transitionForCFS(duration),
            () => {
                // #444
                KennethMustDie.soundPlayer.playSound(KMDSound.CFS_ELEVATOR);
            }
        ]);

        tl.add([
            KennethMustDie.reelView.transitionForCFS(postDuration),
            KennethMustDie.specialReelView.transitionForCFS(postDuration),
            KennethMustDie.multipliersChain.transitionForCFS(postDuration),
            KennethMustDie.possibleWays.transitionForCFS(postDuration),
            KennethMustDie.reelFrame.turnOnForCFS(postDuration),
            KennethMustDie.logo.transitionForCFS(postDuration)
        ]);

        // #505
        tl.add(() => {
            KennethMustDie.soundPlayer.switchMusic(KMDGameMode.FREESPIN_COOL);
        });

        return tl;
    }

    protected getExtraFS(): number {
        return 1;
    }

    protected hideXHExplainFeature(): gsap.core.Timeline {
        return gsap.timeline();
    }

    private xNudgeFeatureExplain(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            symId: number = MathHelper.randomNumberInRange(1, 2),
            xNudge: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD)),
            lock: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("locks")),
            animName: string = "up_" + symId + "_step_nowin";
        let reelId: number = MathHelper.randomNumberInRange(1, 3);

        if ((KennethMustDie.model.gameData.nextMode === KMDGameMode.FREESPIN_SUPER) && (reelId === 2)) {
            reelId = ArrayHelper.getRandomValue([1, 3]); // excluding reel 2
        }

        xNudge.position.set(((reelId * KennethMustDie.SYMBOL_WIDTH) + KennethMustDie.SYMBOL_WIDTH / 2), (symId * KennethMustDie.SYMBOL_HEIGHT) + KennethMustDie.MAIN_REELS_TOP);
        lock.position.x = xNudge.position.x;
        lock.position.y = xNudge.position.y - (symId === 1 ? (KennethMustDie.SYMBOL_HEIGHT / 2) : (KennethMustDie.SYMBOL_HEIGHT));

        tl.to(this, {
            duration: 0.1
        });
        tl.add(() => {
            xNudge.state.setAnimation(0, animName, false).trackTime = xNudge.skeleton.data.findAnimation(animName).duration;

            this.addChild(xNudge, lock);

            // #479
            KennethMustDie.soundPlayer.playSound(KMDSound.SYM_CHAIN_ADD);
        });
        tl.add(KennethMustDie.createTimelineForAnimation(lock, 0, "locked_" + (symId === 1 ? "300" : "450") + "_start", false));
        tl.to(this, {
            duration: 0.4
        });

        tl.add(this.hideXHExplainFeature());
        tl.to([xNudge, lock, this._featureExplainS2, this._featureExplainS2Lock], {
            duration: 0.2,
            alpha: 0
        }, "<");

        tl.add(() => {
            this.removeChild(xNudge, lock);

            this._featureExplainS2.visible = this._featureExplainS2Lock.visible = false;
        });

        return tl;
    }
}